/**
 * CustomerBonMessageButton — Bouton admin (icône + label) pour éditer le petit mot
 * qui s'affiche sur TOUS les bons client émis (ticket public scanné via QR,
 * PDF facture, etc.).
 *
 * - Réservé à l'admin
 * - Le badge violet pulse quand un message est actif
 * - Modale simple : textarea + boutons Enregistrer / Effacer
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquareText, X, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PRESETS = [
  "Bonne fête de l'indépendance !",
  "Merci pour votre visite — à bientôt !",
  "Ouverts tous les jours de 8h à minuit",
  "Promo -20% sur les cocktails cette semaine",
  "Le Wi-Fi est disponible sur demande",
];

const CustomerBonMessageButton = ({ adminName = "Admin", isAdmin = false, dataTestId = "customer-bon-msg-btn" }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ active: false, message: "", updated_by: "", updated_at: null });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/customer-bon-message`);
      setState(r.data);
      setDraft(r.data.message || "");
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (isAdmin) fetchState(); }, [isAdmin, fetchState]);

  if (!isAdmin) return null;

  const save = async () => {
    const msg = (draft || "").trim();
    if (msg.length === 0) { toast.error("Le message est vide — utilisez Effacer pour retirer complètement."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/customer-bon-message`, { message: msg, active: true, updated_by: adminName });
      toast.success("Message client mis à jour");
      setOpen(false);
      await fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur enregistrement");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/customer-bon-message`, { params: { updated_by: adminName } });
      toast.success("Message effacé — plus rien ne s'affichera sur les bons client");
      setDraft("");
      setOpen(false);
      await fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur effacement");
    } finally { setBusy(false); }
  };

  const active = state.active && (state.message || "").trim().length > 0;

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => { setDraft(state.message || ""); setOpen(true); }}
        className={`relative h-9 flex items-center gap-1.5 transition-colors ${
          active
            ? "text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10"
            : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/40"
        }`}
        title={active ? `Message client actif : "${state.message}"` : "Petit mot sur les bons client"}
        data-testid={dataTestId}
      >
        <MessageSquareText className={`w-4 h-4 ${active ? "animate-pulse" : ""}`} strokeWidth={2.4} />
        <span className="hidden sm:inline text-[12px] font-bold">{active ? "Mot actif" : "Petit mot"}</span>
        {active && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-fuchsia-500 border border-slate-900" />
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-slate-900 border-fuchsia-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 34rem)" }}
          data-testid={`${dataTestId}-modal`}
        >
          <DialogHeader>
            <DialogTitle className="text-fuchsia-300 flex items-center gap-2 text-lg">
              <MessageSquareText className="w-5 h-5" />
              Petit mot sur les bons client
            </DialogTitle>
            <p className="text-slate-400 text-[12px] pt-1 leading-snug">
              Ce message apparaîtra sur TOUS les bons client (ticket public scanné via QR + PDF facture).
              Idéal pour un vœu, une promo, un rappel d&apos;horaire. Laissez vide pour retirer.
            </p>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Message</label>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                maxLength={240}
                placeholder="Ex : Bonne fête de l'indépendance !"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-fuchsia-500/60 focus:outline-none"
                data-testid={`${dataTestId}-textarea`}
              />
              <p className={`mt-1 text-[10.5px] tabular-nums ${draft.length > 200 ? "text-amber-400" : "text-slate-500"}`}>
                {draft.length} / 240 caractères
              </p>
            </div>

            <div>
              <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider mb-1">Suggestions</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setDraft(p)}
                    className="text-[11px] px-2 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-fuchsia-500/40 hover:text-fuchsia-200 transition-colors"
                    data-testid={`${dataTestId}-preset`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Aperçu live du rendu client */}
            {draft.trim() && (
              <div>
                <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider mb-1">Aperçu sur le bon client</p>
                <div className="bg-gradient-to-r from-purple-600 to-fuchsia-700 text-white rounded-xl p-3 border border-purple-400/50">
                  <p className="text-center text-[14px] font-black leading-snug tracking-wide">
                    {draft.trim()}
                  </p>
                </div>
              </div>
            )}

            {state.updated_at && (
              <p className="text-slate-500 text-[10.5px] italic">
                Dernière modification : {new Date(state.updated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · par {state.updated_by || "Admin"}
              </p>
            )}
          </div>

          <div className="flex gap-2 justify-between pt-2 border-t border-slate-700 flex-wrap">
            <Button
              variant="outline"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
              onClick={clear}
              disabled={busy || !active}
              data-testid={`${dataTestId}-clear`}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Effacer
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setOpen(false)} data-testid={`${dataTestId}-cancel`}>
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-bold"
                onClick={save}
                disabled={busy || (draft || "").trim().length === 0}
                data-testid={`${dataTestId}-save`}
              >
                <Save className="w-4 h-4 mr-1" /> Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerBonMessageButton;
